import React from "react";
import { usePermission } from "../../helpers/usePermission";

export const TOKEN = "$WANK";

export function shortAddr(a) {
  if (!a) return "-";
  return a.length > 14 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a;
}
export function fmtNum(n) {
  return Number(n || 0).toLocaleString();
}
export function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

export const card = { background: "var(--bl-surface, #1b1f27)", border: "1px solid var(--bl-border, #2a2f3a)", borderRadius: 12, padding: 20, marginBottom: 16 };
export const btn = { padding: "8px 14px", borderRadius: 8, border: "1px solid var(--bl-border, #2a2f3a)", background: "var(--bl-accent, #6c5ce7)", color: "#fff", cursor: "pointer", fontWeight: 600 };
export const btnGhost = { ...btn, background: "transparent", color: "var(--bl-text, #cdd3df)" };
export const input = { padding: "8px 10px", borderRadius: 8, border: "1px solid var(--bl-border, #2a2f3a)", background: "var(--bl-bg, #11141a)", color: "var(--bl-text, #cdd3df)", width: "100%" };
export const th = { textAlign: "left", padding: "8px 10px", color: "var(--bl-muted, #8b93a7)", fontWeight: 600, fontSize: 12, textTransform: "uppercase" };
export const td = { padding: "10px", borderTop: "1px solid var(--bl-border, #2a2f3a)" };
export const label = { color: "var(--bl-muted, #8b93a7)", fontSize: 12 };
export const COLORS = { error: "#ff6e6e", success: "#00cd52", warn: "#f5b041" };

// Where a rewards item shows in the app (one design since 2026-10-26;
// ADMIN_PANEL_MAP.md M13-M16). `warn` marks content the current app build
// cannot show yet, so nobody publishes it expecting users to see it.
export function AppPlacement({ children, warn = false }) {
  return (
    <p style={{ ...label, marginTop: 0, color: warn ? COLORS.warn : label.color }}>
      <strong>{warn ? "Not in the current app yet: " : "In the app: "}</strong>
      {children}
    </p>
  );
}

export const busyBtn = (busy, base = btn) => ({ ...base, opacity: busy ? 0.6 : 1 });

// Permission set for the rewards page. rewards.manage is the superset that
// still grants every write (matches the backend's "<fine key> OR manage").
export function useRewardsPerms() {
  const can = usePermission();
  const manage = can("rewards.manage");
  return {
    view: can("rewards.view"),
    drops: manage || can("rewards.drops"),
    payouts: manage || can("rewards.payouts"),
    price: manage || can("rewards.price"),
    replay: manage || can("rewards.replay"),
  };
}

// ── Inline notice banner (no toast lib in this app) ──────────────────────────
export function Notice({ notice, onClose }) {
  if (!notice) return null;
  const color = notice.type === "error" ? COLORS.error : notice.type === "success" ? COLORS.success : "var(--bl-accent, #6c5ce7)";
  return (
    <div style={{ ...card, borderColor: color, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ color }}>{notice.text}</span>
      <button style={{ ...btnGhost, padding: "2px 8px" }} onClick={onClose} aria-label="Dismiss">✕</button>
    </div>
  );
}

export function Banner({ tone = "warn", children }) {
  const color = COLORS[tone] || COLORS.warn;
  return (
    <div style={{ ...card, borderColor: color, color }}>{children}</div>
  );
}

export function Badge({ tone = "warn", children }) {
  const color = COLORS[tone] || COLORS.warn;
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, border: `1px solid ${color}`, color, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

export function Muted({ children }) {
  return <span style={{ color: "var(--bl-muted, #8b93a7)" }}>{children}</span>;
}

export function ReadOnlyNote({ children }) {
  return <p style={{ ...label, marginTop: 10 }}>{children || "Read only. Your role cannot make changes here."}</p>;
}

export function Stat({ label: l, value }) {
  return (
    <div>
      <div style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 12 }}>{l}</div>
      <div style={{ fontWeight: 700, fontSize: 18 }}>{value}</div>
    </div>
  );
}

export function StatGrid({ children, min = 150 }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 14 }}>{children}</div>;
}

export function Pagination({ page, totalPages, total, onPage }) {
  if (!totalPages || totalPages <= 1) {
    return <div style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 12, marginTop: 10 }}>{fmtNum(total)} total</div>;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
      <button style={{ ...btnGhost, opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      <span style={{ color: "var(--bl-muted, #8b93a7)", fontSize: 13 }}>Page {page} / {totalPages} · {fmtNum(total)} total</span>
      <button style={{ ...btnGhost, opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

export function SearchBar({ value, onChange, onSubmit, onRefresh, placeholder }) {
  return (
    <form onSubmit={onSubmit} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <input style={{ ...input, width: 280 }} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      <button style={btn} type="submit">Search</button>
      <button style={btnGhost} type="button" onClick={onRefresh}>Refresh</button>
    </form>
  );
}

export function Table({ head, children }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{head.map((h, i) => <th key={`${h}-${i}`} style={th}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// Page meta from a paginated list response.
export function pageMetaOf(res) {
  return { page: res?.page || 1, total_pages: res?.total_pages || 1, total: res?.total ?? res?.count ?? 0 };
}

// Read a text file chosen in an <input type="file"> into a string.
export function readFileText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(new Error("Could not read the file."));
    r.readAsText(file);
  });
}
